import React from 'react';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';

type Variant = 'primary' | 'gold' | 'outline' | 'ghost' | 'light';
type Size = 'sm' | 'md' | 'lg';

interface BaseProps {
  children: React.ReactNode;
  variant?: Variant;
  size?: Size;
  className?: string;
  /** Affiche une flèche animée à droite. */
  withArrow?: boolean;
}

interface LinkProps extends BaseProps {
  href: string;
  external?: boolean;
  onClick?: never;
  type?: never;
}

interface ButtonProps extends BaseProps {
  href?: never;
  onClick?: () => void;
  type?: 'button' | 'submit';
}

const VARIANTS: Record<Variant, string> = {
  // Noir profond — action principale sur fond clair.
  primary:
    'bg-ink text-white border border-ink hover:bg-ink-700 shadow-luxe-sm hover:shadow-luxe',
  // Action principale claire sur fond sombre (ex-« or », désormais blanc pur).
  gold: 'bg-white text-ink border border-white hover:bg-gold-100 shadow-luxe',
  // Contour discret sur fond clair.
  outline:
    'bg-transparent text-ink border border-ink/15 hover:border-ink/40 hover:bg-ink/[0.03]',
  // Contour clair sur fond sombre (héros).
  light:
    'bg-white/5 text-white border border-white/25 hover:bg-white/10 hover:border-white/50 backdrop-blur-sm',
  // Lien-bouton minimal.
  ghost: 'bg-transparent text-ink border border-transparent hover:text-gold-600',
};

const SIZES: Record<Size, string> = {
  sm: 'text-[12.5px] px-4 py-2 gap-1.5',
  md: 'text-[13.5px] px-6 py-3 gap-2',
  lg: 'text-[15px] px-8 py-4 gap-2.5',
};

function classes(variant: Variant, size: Size, className?: string): string {
  return [
    'group inline-flex items-center justify-center rounded-full font-medium tracking-tight',
    'transition-all duration-300 ease-out active:scale-[0.98]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
    VARIANTS[variant],
    SIZES[size],
    className ?? '',
  ].join(' ');
}

function Inner({ children, withArrow }: { children: React.ReactNode; withArrow?: boolean }) {
  return (
    <>
      <span data-component-id="src/components/marketing/ui/Button.tsx:66:6" data-component-path="src/components/marketing/ui/Button.tsx" data-component-line="66" data-component-end-line="66" data-component-file="Button.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{children}</span>
      {withArrow && (
        <ArrowRight data-component-id="src/components/marketing/ui/Button.tsx:68:8" data-component-path="src/components/marketing/ui/Button.tsx" data-component-line="68" data-component-end-line="71" data-component-file="Button.tsx" data-component-name="ArrowRight" data-component-content="%7B%22elementName%22%3A%22ArrowRight%22%2C%22className%22%3A%22transition-transform%20duration-300%20ease-out%20group-hover%3Atranslate-x-1%22%7D"
          size={16}
          className="transition-transform duration-300 ease-out group-hover:translate-x-1"
        />
      )}
    </>
  );
}

export function Button(props: LinkProps | ButtonProps) {
  const { children, variant = 'primary', size = 'md', className, withArrow } = props;

  if ('href' in props && props.href) {
    if (props.external) {
      return (
        <a data-component-id="src/components/marketing/ui/Button.tsx:83:8" data-component-path="src/components/marketing/ui/Button.tsx" data-component-line="83" data-component-end-line="90" data-component-file="Button.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%7D"
          href={props.href}
          target="_blank"
          rel="noopener noreferrer"
          className={classes(variant, size, className)}
        >
          <Inner data-component-id="src/components/marketing/ui/Button.tsx:89:10" data-component-path="src/components/marketing/ui/Button.tsx" data-component-line="89" data-component-end-line="89" data-component-file="Button.tsx" data-component-name="Inner" data-component-content="%7B%22elementName%22%3A%22Inner%22%7D" withArrow={withArrow}>{children}</Inner>
        </a>
      );
    }
    return (
      <Link data-component-id="src/components/marketing/ui/Button.tsx:94:6" data-component-path="src/components/marketing/ui/Button.tsx" data-component-line="94" data-component-end-line="96" data-component-file="Button.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D" href={props.href} className={classes(variant, size, className)}>
        <Inner data-component-id="src/components/marketing/ui/Button.tsx:95:8" data-component-path="src/components/marketing/ui/Button.tsx" data-component-line="95" data-component-end-line="95" data-component-file="Button.tsx" data-component-name="Inner" data-component-content="%7B%22elementName%22%3A%22Inner%22%7D" withArrow={withArrow}>{children}</Inner>
      </Link>
    );
  }

  const { onClick, type = 'button' } = props as ButtonProps;
  return (
    <button data-component-id="src/components/marketing/ui/Button.tsx:102:4" data-component-path="src/components/marketing/ui/Button.tsx" data-component-line="102" data-component-end-line="104" data-component-file="Button.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22%5Bvar%3Atype%5D%22%7D" type={type} onClick={onClick} className={classes(variant, size, className)}>
      <Inner data-component-id="src/components/marketing/ui/Button.tsx:103:6" data-component-path="src/components/marketing/ui/Button.tsx" data-component-line="103" data-component-end-line="103" data-component-file="Button.tsx" data-component-name="Inner" data-component-content="%7B%22elementName%22%3A%22Inner%22%7D" withArrow={withArrow}>{children}</Inner>
    </button>
  );
}
